import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Box, LinearProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Order } from '../../../app/models/order';
import agent from '../../../app/api/agent';
import { toast } from 'react-toastify';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useAppDispatch, useAppSelector } from '../../../app/store/configureStore';
import { fetchAdminOrderAsync } from './adminOrdersSlice';

interface TestAvailabilityResponse {
    PackageTestPath?: string | boolean;
    PackageTestKeyPath?: string | boolean;
    QuestionsJson?: string | any[];
    isGraphical: boolean;
}

interface TestAvailabilityAlertsProps {
    order: Order;
    onUpdateServiceToOrder: () => void;
    onStatusChange?: (hasTests: boolean) => void;
    onGeneratingChange?: (isGenerating: boolean) => void;
    onGenerationComplete?: () => void;
}

/**
 * Komponent zarządzający stanem i wyświetlaniem alertów odnośnie dostępności testów
 */
export default function AdminTestAvailabilityAlerts({
    order,
    onUpdateServiceToOrder,
    onStatusChange,
    onGeneratingChange,
    onGenerationComplete
}: TestAvailabilityAlertsProps) {
    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.account.user);
    const isSuperAdmin = !!(user && user.roles?.includes('SuperAdmin'));
    
    // Stany przeniesione z komponentu nadrzędnego
    const [isLoading, setIsLoading] = useState(true);
    const [isUpdating, setIsUpdating] = useState(false);
    const [hasTestPath, setHasTestPath] = useState(false);
    const [hasKeyTestPath, setHasKeyTestPath] = useState(false);
    const [hasQuestionsJson, setHasQuestionsJson] = useState(false);
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    // Lokalny stan śledzący czy generowanie jest w toku (kontroluje polling)
    const [isGeneratingInProgress, setIsGeneratingInProgress] = useState(false);
    // Confirmation dialog state
    const [showRegenerateConfirmDialog, setShowRegenerateConfirmDialog] = useState(false);

    // Uruchom polling gdy order ma flagę isTestGenerationInProgress = true (przy wejściu na stronę)
    useEffect(() => {
        if (order.isTestGenerationInProgress && !isGeneratingInProgress) {
            setIsGeneratingInProgress(true);
        }
    }, [order.isTestGenerationInProgress]); // eslint-disable-line react-hooks/exhaustive-deps

    // Informuj komponent nadrzędny o zmianie stanu generowania
    useEffect(() => {
        if (onGeneratingChange) {
            onGeneratingChange(isGeneratingInProgress);
        }
    }, [isGeneratingInProgress, onGeneratingChange]);

    // Polling statusu generowania co 20 sekund
    useEffect(() => {
        if (!isGeneratingInProgress) {
            return;
        }

        const checkStatus = async () => {
            try {
                const response = await agent.AI.getTestGenerationStatus(order.publicId);
                
                // DEBUG: Loguj odpowiedź z endpointu
                console.log('=== STATUS CHECK ===');
                console.log('Response from API:', response);
                console.log('Current order state:', {
                    publicId: order.publicId,
                    isTestGenerationInProgress: order.isTestGenerationInProgress,
                    questionsJson: order.questionsJson
                });
                
                if (!response.isGenerating) {
                    // Generowanie zakończone
                    setIsGeneratingInProgress(false);
                    
                    // SuperAdmin widzi informację o AI, Admin widzi neutralny komunikat
                    if (isSuperAdmin) {
                        toast.success('Pytania testowe zostały wygenerowane przez AI!');
                    } else {
                        toast.success('Weryfikacja zakończona - możesz zarządzać zamówieniem.');
                    }
                    
                    dispatch(fetchAdminOrderAsync(order.publicId));
                    
                    // Powiadom komponent nadrzędny o zakończeniu generowania (np. do odświeżenia historii)
                    if (onGenerationComplete) {
                        onGenerationComplete();
                    }
                    
                    // DEBUG: Pobierz aktualny order z API
                    console.log('=== FETCHING FULL ORDER ===');
                    const fullOrder = await agent.Admin.Orders.details(order.publicId);
                    console.log('Full order after generation:', fullOrder);
                    console.log('questionsJson:', fullOrder.questionsJson);
                } else {
                    // Wciąż trwa generowanie
                    if (isSuperAdmin) {
                        toast.info('Generowanie pytań wciąż trwa...', {
                            autoClose: 3000,
                            toastId: 'generating-in-progress'
                        });
                    } else {
                        toast.info('Weryfikacja wciąż trwa...', {
                            autoClose: 3000,
                            toastId: 'generating-in-progress'
                        });
                    }
                }
            } catch (error) {
                console.error('Error checking generation status:', error);
                setIsGeneratingInProgress(false);
                if (isSuperAdmin) {
                    toast.error('Błąd podczas sprawdzania statusu generowania');
                } else {
                    toast.error('Błąd podczas sprawdzania statusu weryfikacji');
                }
            }
        };

        // Ustaw interval co 20 sekund
        const intervalId = setInterval(checkStatus, 20000);

        // Cleanup przy odmontowaniu lub zmianie isGeneratingInProgress
        return () => {
            clearInterval(intervalId);
        };
    }, [isGeneratingInProgress, order.publicId, dispatch]);

    // Check if order has custom exam name (different from service name)
    const hasCustomExamName = useCallback((): boolean => {
        return !!(order.customeClientServiceName && 
            order.customeClientServiceName.trim() !== '' &&
            order.customeClientServiceName.trim().toLowerCase() !== order.serviceName?.trim().toLowerCase());
    }, [order.customeClientServiceName, order.serviceName]);

    // Helper to check if education effects have actual content (not just whitespace/semicolons)
    const hasValidEducationEffects = useCallback((): boolean => {
        if (!order.educationEffects) return false;
        const cleaned = order.educationEffects.trim().replace(/;+/g, '').trim();
        return cleaned.length > 0;
    }, [order.educationEffects]);

    // Check if order has required data for AI generation (SuperAdmin only)
    const canGenerateAI = useCallback((): boolean => {
        if (!isSuperAdmin) return false; // Only SuperAdmin can generate AI
        return hasCustomExamName() && 
            !!(order.trainingProgram && order.trainingProgram.trim() !== '') &&
            hasValidEducationEffects();
    }, [hasCustomExamName, order.trainingProgram, hasValidEducationEffects, isSuperAdmin]);

    // Handle AI test generation with optional confirmation
    const handleGenerateAIClick = () => {
        const hasOrderQuestions = hasValidQuestionsJson();
        if (hasOrderQuestions) {
            // Show confirmation dialog when questions already exist
            setShowRegenerateConfirmDialog(true);
        } else {
            // Generate directly when no questions exist
            handleGenerateAIQuestions();
        }
    };

    // Handle confirmation dialog close
    const handleCloseRegenerateConfirmDialog = () => {
        setShowRegenerateConfirmDialog(false);
    };

    // Handle confirmed regeneration
    const handleConfirmRegenerate = () => {
        setShowRegenerateConfirmDialog(false);
        handleGenerateAIQuestions();
    };

    // Handle AI test generation
    const handleGenerateAIQuestions = async () => {
        setIsGeneratingAI(true);
        try {
            await agent.AI.generateTestQuestions(order.publicId);
            // Ustaw lokalny stan generowania - polling przejmie kontrolę
            setIsGeneratingInProgress(true);
            toast.info('Rozpoczęto generowanie pytań. Status będzie sprawdzany co 20 sekund.', {
                autoClose: 5000
            });
        } catch (error: any) {
            console.error('Error generating AI questions:', error);
            const errorMessage = error?.data?.error || 'Nie udało się zlecić generowania pytań.';
            toast.error(errorMessage);
        } finally {
            setIsGeneratingAI(false);
        }
    };

    // Funkcje pomocnicze do sprawdzania danych
    const isEmptyOrNull = (value: any): boolean => {
        return value === null || value === undefined || value === '' || value === '{}';
    };

    // Funkcja sprawdzająca, czy zamówienie ma już pytania JSON - tylko dla niegraficznych usług
    const hasValidQuestionsJson = useCallback((): boolean => {
        // Jeśli usługa jest graficzna, nie sprawdzamy questionsJson
        if (order.isGraphical) return false;

        if (!order.questionsJson) {
            return false;
        }

        if (Array.isArray(order.questionsJson)) {
            return order.questionsJson.length > 0;
        }
        else if (typeof order.questionsJson === 'string') {
            if (order.questionsJson.trim() === '' || order.questionsJson === '{}') return false;
            try {
                const parsedJson = JSON.parse(order.questionsJson);
                if (Array.isArray(parsedJson)) return parsedJson.length > 0;
                return Object.keys(parsedJson).length > 0;
            } catch (e) {
                console.error("Błąd parsowania JSON:", e);
                return false;
            }
        }
        else if (typeof order.questionsJson === 'object') {
            return Object.keys(order.questionsJson).length > 0;
        }

        return false;
    }, [order.questionsJson, order.isGraphical]);

    useEffect(() => {
        async function checkTestAvailability() {
            if (order && order.serviceId) {
                try {
                    setIsLoading(true);

                    // Jeśli usługa jest niegraficzna i zamówienie ma już pytania, nie sprawdzamy niczego więcej
                    if (!order.isGraphical && hasValidQuestionsJson()) {
                        setHasQuestionsJson(true);
                        if (onStatusChange) onStatusChange(true);
                        setIsLoading(false);
                        return;
                    }

                    // Jeśli zamówienie nie ma pytań lub jest graficzne, sprawdź usługę
                    const response: TestAvailabilityResponse =
                        await agent.Admin.Orders.checkIfTestAndKeyTestIsInService(order.serviceId);

                    // Sprawdzamy czy isGraphical w odpowiedzi jest zgodne z order.isGraphical
                    // Jeśli nie, przyjmujemy wartość z zamówienia jako nadrzędną
                    if (response.isGraphical !== order.isGraphical) {
                        console.warn("Niezgodność typu usługi między zamówieniem a serwisem");
                    }

                    // Ustaw flagi dostępności na podstawie odpowiedzi z API
                    // Dla usług graficznych sprawdzamy tylko ścieżki do plików
                    if (order.isGraphical) {
                        setHasTestPath(!!response.PackageTestPath);
                        setHasKeyTestPath(!!response.PackageTestKeyPath);
                    }
                    // Dla usług niegraficznych sprawdzamy tylko pytania JSON
                    else {
                        // Dla usług niegraficznych, sprawdź czy są pytania w API
                        let serviceHasQuestions = false;

                        if (response.QuestionsJson) {
                            if (Array.isArray(response.QuestionsJson)) {
                                serviceHasQuestions = response.QuestionsJson.length > 0;
                            } else if (typeof response.QuestionsJson === 'string') {
                                serviceHasQuestions = response.QuestionsJson.trim() !== '' &&
                                    response.QuestionsJson !== '{}';
                                if (serviceHasQuestions) {
                                    try {
                                        const parsed = JSON.parse(response.QuestionsJson);
                                        serviceHasQuestions = Array.isArray(parsed) ? parsed.length > 0 :
                                            Object.keys(parsed).length > 0;
                                    } catch (e) {
                                        serviceHasQuestions = false;
                                    }
                                }
                            } else if (typeof response.QuestionsJson === 'object') {
                                serviceHasQuestions = Object.keys(response.QuestionsJson).length > 0;
                            }
                        }

                        setHasQuestionsJson(serviceHasQuestions);
                    }

                    // Poinformuj komponent nadrzędny o zmianie statusu dostępności testów
                    if (onStatusChange) {
                        if (order.isGraphical) {
                            onStatusChange(!isEmptyOrNull(order.packageTestPath));
                        } else {
                            onStatusChange(hasValidQuestionsJson() || hasQuestionsJson);
                        }
                    }
                } catch (error) {
                    console.error("Error checking test availability:", error);
                    setHasTestPath(false);
                    setHasKeyTestPath(false);

                    // Jeśli wystąpił błąd, ale zamówienie ma pytania, zachowaj ten status
                    if (!order.isGraphical) {
                        const orderHasQuestions = hasValidQuestionsJson();
                        setHasQuestionsJson(orderHasQuestions);
                        if (onStatusChange) {
                            onStatusChange(orderHasQuestions);
                        }
                    } else {
                        if (onStatusChange) {
                            onStatusChange(false);
                        }
                    }
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        }
        checkTestAvailability();
    }, [order, hasValidQuestionsJson, onStatusChange, hasQuestionsJson]);

    // Funkcja opakowująca handler aktualizacji
    const handleUpdateServiceToOrder = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        setIsUpdating(true);

        // Wywołaj funkcję z komponentu nadrzędnego
        onUpdateServiceToOrder();

        // Zakończ aktualizację po krótkim czasie lub zmień to na Promise w komponencie nadrzędnym
        setTimeout(() => setIsUpdating(false), 1000);
    };

    if (isLoading || isUpdating) {
        return <LinearProgress sx={{ mb: 2, mr: 2 }} />;
    }

    // Dla usług graficznych
    if (order.isGraphical) {
        const hasPackageTestPath = !isEmptyOrNull(order.packageTestPath);
        const hasPackageTestKeyPath = !isEmptyOrNull(order.packageTestKeyPath);

        // Jeśli zamówienie ma już test i klucz - wyświetl komunikat sukcesu
        if (hasPackageTestPath && hasPackageTestKeyPath) {
            return (
                <Alert sx={{ mb: 2, mr: 2 }} severity="success">
                    Zamówienie posiada zarówno arkusz egzaminacyjny jak i klucz odpowiedzi.
                </Alert>
            );
        }

        return (
            <>
                {/* Komunikaty dla usług graficznych */}
                {!hasPackageTestPath && hasTestPath && (
                    <Alert sx={{ mb: 2, mr: 2 }} severity="info">
                        Dostępny jest test dla usługi "{order.serviceName}".{' '}
                        <a href="#" onClick={handleUpdateServiceToOrder}>
                            Kliknij tutaj, aby dodać test do zamówienia.
                        </a>
                    </Alert>
                )}

                {!hasPackageTestKeyPath && hasKeyTestPath && (
                    <Alert sx={{ mb: 2, mr: 2 }} severity="info">
                        Dostępny jest klucz odpowiedzi dla usługi "{order.serviceName}".{' '}
                        <a href="#" onClick={handleUpdateServiceToOrder}>
                            Kliknij tutaj, aby dodać klucz odpowiedzi do zamówienia.
                        </a>
                    </Alert>
                )}

                {/* Komunikat o braku testu */}
                {!hasPackageTestPath && !hasTestPath && (
                    <Box>
                        <Alert severity="error" sx={{ mr: { xs: 0, md: 2 }, mb: 2 }}>
                            To zamówienie nie posiada wzoru arkusza egzaminu. Skorzystaj z przycisków 
                        </Alert>
                    </Box>
                )}

                {/* Komunikat o braku klucza */}
                {!hasPackageTestKeyPath && !hasKeyTestPath && (
                    <Box>
                        <Alert severity="warning" sx={{ mr: { xs: 0, md: 2 }, mb: 2 }}>
                            To zamówienie nie posiada klucza odpowiedzi.
                        </Alert>
                    </Box>
                )}
            </>
        );
    }
    // Dla usług niegraficznych
    else {
        const hasOrderQuestions = hasValidQuestionsJson();
        // Używamy lokalnego stanu który jest aktualizowany przez lekki endpoint
        const isGenerating = isGeneratingInProgress;

        return (
            <>
                {/* Komunikat gdy pytania są dostępne w usłudze, ale nie w zamówieniu */}
                {/* Pokazuj tylko gdy zamówienie NIE MA pytań (!hasOrderQuestions) ale usługa MA pytania (hasQuestionsJson) */}
                {!isGenerating && !hasOrderQuestions && hasQuestionsJson && (
                    <Alert sx={{ mb: 2, mr: 2 }} severity="info">
                        Dostępne są pytania egzaminacyjne dla usługi "{order.serviceName}".{' '}
                        <a href="#" onClick={handleUpdateServiceToOrder}>
                            Kliknij tutaj, aby dodać pytania do zamówienia.
                        </a>
                    </Alert>
                )}

                {/* Komunikat o braku pytań */}
                {!isGenerating && !hasOrderQuestions && !hasQuestionsJson && !canGenerateAI() && (
                    <Box>
                        <Alert severity="error" sx={{ mr: { xs: 0, md: 2 }, mb: 2 }}>
                            Usługa nie posiada pytań egzaminacyjnych. Udostępnienie testów będzie możliwe po dodaniu pytań do odpowiedniej usługi. {' '}
                            <a href={'/services/' + order.serviceId}>Przejdź do usługi</a>
                        </Alert>
                    </Box>
                )}

                {/* Przycisk do generowania pytań AI - dostępny tylko dla SuperAdmin z własnową nazwą egzaminu, programem szkolenia i efektami kształcenia */}
                {canGenerateAI() && (
                    <Box sx={{ mb: 2, mr: 2 }}>
                        <Alert 
                            severity={isGenerating ? "info" : hasOrderQuestions ? "warning" : "info"} 
                            action={
                                <LoadingButton
                                    color="inherit"
                                    size="small"
                                    onClick={handleGenerateAIClick}
                                    loading={isGeneratingAI || isGenerating}
                                    disabled={isGeneratingAI || isGenerating}
                                    loadingPosition="start"
                                    startIcon={<AutoFixHighIcon />}
                                >
                                    {isGenerating ? 'Generowanie w toku...' : isGeneratingAI ? 'Zlecanie...' : hasOrderQuestions ? 'Wygeneruj ponownie' : 'Wygeneruj pytania AI'}
                                </LoadingButton>
                            }
                        >
                            {isGenerating 
                                ? 'Pytania testowe są aktualnie generowane w tle. Status jest sprawdzany automatycznie co 20 sekund.'
                                : hasOrderQuestions
                                    ? `Pytania już istnieją. Możesz wygenerować nowe pytania AI dla "${order.customeClientServiceName}" - zastąpią one obecne.`
                                    : `To zamówienie ma własną nazwę egzaminu "${order.customeClientServiceName}". Możesz wygenerować pytania testowe automatycznie przy użyciu AI.`
                            }
                        </Alert>
                    </Box>
                )}

                {/* Informacja gdy brak możliwości generowania AI - tylko dla SuperAdmin */}
                {isSuperAdmin && !isGenerating && !hasOrderQuestions && hasCustomExamName() && !canGenerateAI() && (
                    <Box sx={{ mb: 2, mr: 2 }}>
                        <Alert severity="warning">
                            Zamówienie ma własną nazwę egzaminu, ale brakuje{' '}
                            {!order.trainingProgram || order.trainingProgram.trim() === '' ? 'programu szkolenia' : ''}
                            {(!order.trainingProgram || order.trainingProgram.trim() === '') && !hasValidEducationEffects() ? ' i ' : ''}
                            {!hasValidEducationEffects() ? 'efektów kształcenia' : ''}.{' '}
                            Uzupełnij brakujące dane aby móc wygenerować pytania AI.
                        </Alert>
                    </Box>
                )}

                {/* Confirmation dialog for regeneration */}
                <Dialog
                    open={showRegenerateConfirmDialog}
                    onClose={handleCloseRegenerateConfirmDialog}
                >
                    <DialogTitle>Potwierdź regenerację pytań</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Pytania testowe już istnieją dla tego zamówienia. Wygenerowanie nowych pytań AI zastąpi istniejące pytania.
                            <br /><br />
                            Czy na pewno chcesz kontynuować?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseRegenerateConfirmDialog} color="primary">
                            Anuluj
                        </Button>
                        <Button onClick={handleConfirmRegenerate} color="warning" variant="contained">
                            Tak, wygeneruj ponownie
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
}